/**
 * FOLLOWING A COMPLETED TRANSFER.
 *
 * An attended transfer moves you from the consult call into the original one
 * without ringing you again — you are already talking to the person handing you
 * over, so a second incoming card would be theatre. What you DO need is to
 * change rooms, and `call.transferred` on your own `user:` topic is the only
 * thing that says so: the consult call ends a moment later, and without this
 * the transferred party would sit on a "Call ended" screen while the call they
 * were handed carried on without them.
 */
import { useEffect } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getRealtimeClient } from '@/lib/realtime';
import { useOpenCall } from '../../hooks/useOpenCall';

export function useTransferredCall(): void {
  const openCall = useOpenCall();
  const { user } = useAuth();
  const meId = user?.id ?? null;

  useEffect(() => {
    if (meId === null) return undefined;
    const realtime = getRealtimeClient();
    realtime.connect();
    return realtime.onEvent(({ event }) => {
      /* `call.merged` needs exactly the same following: a merge moves the other
         call's people into this one without ringing them, so their client is
         told where to go by the roster in this event and nothing else. */
      if (event.type !== 'call.transferred' && event.type !== 'call.merged') return;
      /* Only if it landed ME in the call. The same event reaches the transferor,
         who has just left it, and everybody who was already on the surviving
         call — for whom this navigation is a no-op. */
      const mine = event.call.participants.some(
        (party) => party.user?.id === meId && party.state === 'connected',
      );
      /* THE SAME RULE AS ANSWERING, decided in one place. This hook is
         mounted by IncomingCallHost, which lives in AppOverlays — the
         WORKSPACE window — so on the desktop it must hand the call to the
         shell rather than drag the workspace into /call/:id, where it would
         try to join a call another window already holds and land on "This call
         is no longer available". The window is named per call, so a call that
         is already open is FOCUSED, not opened twice. */
      if (mine) {
        openCall({
          callId: event.callId,
          /* The roster is on the event, but who counts as "the other person"
             on a merged call is not a question with one answer. The window
             takes its title from the room once it has joined. */
          peerName: '',
          kind: event.call.kind,
          startsConnected: true,
        });
      }
    });
  }, [meId, openCall]);
}
